import React, { useState, useContext, useEffect, useMemo } from 'react'
import { NavigationContext } from '../../../context/navigation-context'
import useFetch from '../../../hooks/fetchMSSQL-hook'
import useForm from '../../../hooks/form-hook'
import TagForm from '../../../components/Modals/TagForm'
import { LangContext } from '../../../context/lang-context'
import AcceptForm from '../../../components/Modals/AcceptForm'
import { NotificationManager } from 'react-notifications'
import SearchFilter from '../../../components/Filter/SearchFilter'

const Tags = () => {
  const { fetchMssqlApi } = useFetch()
  const { TRL_Pack } = useContext(LangContext)
  const { setHeaderData } = useContext(NavigationContext)

  const [tagLabel, setTagLabel] = useState()
  const [tagRows, setTagRows] = useState([])
  const [typeFilter, setTypeFilter] = useState('all')
  const [formSection, setFormSection] = useState('machine')
  const [selectedTags, setSelectedTags] = useState([])
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)

  const defaultFilter = {
    name: 'tags',
    page: 1,
    rowsPerPage: 50,
    tagName: '',
    isActive: 'null'
  }

  const [filter, setFilter] = useState(defaultFilter)
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: false,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50
  })

  const { form, openForm, closeForm } = useForm()

  const typeBySection = {
    machine: 1,
    product: 2
  }

  const sectionByType = {
    1: 'machine',
    2: 'product'
  }

  const toBool = value => value === true || value === 1 || value === '1' || value === 'true'

  const parseName = name => {
    const value = String(name || '')
    const idx = value.indexOf(' - ')

    if (idx === -1) {
      return {
        label: null,
        option: value
      }
    }

    return {
      label: value.slice(0, idx).trim(),
      option: value.slice(idx + 3).trim()
    }
  }

  const getTags = (f, pageSize, page) => {
    const currentFilter = {
      ...filter,
      ...(f || {}),
      page: page || f?.page || filter.page || 1,
      rowsPerPage: pageSize || f?.rowsPerPage || filter.rowsPerPage || 50
    }

    const data = {
      tagName: currentFilter.tagName ? String(currentFilter.tagName).trim() : '',
      isActive: currentFilter.isActive
    }

    fetchMssqlApi('tags/list', { method: 'POST', hideNotification: true, data }, rows => {
      setTagRows(rows || [])
    })
  }

  const tags = useMemo(() => {
    const result = {
      machine: [{ section: 'machine', others: true, options: [] }],
      product: [{ section: 'product', others: true, options: [] }]
    }

    ;(tagRows || []).forEach(row => {
      const sectionName = sectionByType[Number(row.Type)]
      if (!sectionName) return

      const parsed = parseName(row.Name)

      const option = {
        tagId: String(row.TagId),
        name: parsed.option,
        isActive: toBool(row.IsActive)
      }

      if (!parsed.label) {
        result[sectionName][0].options.push(option)
        return
      }

      let group = result[sectionName].find(tag => tag.label === parsed.label)

      if (!group) {
        group = { section: sectionName, label: parsed.label, options: [] }
        result[sectionName].push(group)
      }

      group.options.push(option)
    })

    return {
      machine: result.machine.filter(tag => tag.others ? tag.options.length : true),
      product: result.product.filter(tag => tag.others ? tag.options.length : true)
    }
  }, [tagRows])

  const tagGroups = useMemo(() => [
    ...tags.machine,
    ...tags.product
  ], [tags])

  const getTypeName = section => {
    if (section === 'machine') return TRL_Pack.tags.machine || 'Automat'
    if (section === 'product') return TRL_Pack.tags.product || 'Produkt'

    return 'Wszystkie'
  }

  const getTagName = tag => tag.others ? TRL_Pack.tags.others : tag.label

  const getGroupKey = tag => tag.others ? `${tag.section}|others` : `${tag.section}|${tag.label}`

  const getActiveCount = tag => (tag.options || []).filter(option => toBool(option.isActive)).length

  const getAllTagIds = tag => (tag.options || []).map(option => option.tagId).filter(Boolean)

  const rowsPerPage = Number(settingsFilter.rowsPerPage || filter.rowsPerPage || 50)

  const showColumnFilters = settingsFilter?.additionalFilter !== false

  const filteredTags = useMemo(() => {
    return tagGroups.filter(tag => typeFilter === 'all' || tag.section === typeFilter)
  }, [tagGroups, typeFilter])

  const visibleTags = filteredTags.slice(
    (Number(filter.page || 1) - 1) * rowsPerPage,
    Number(filter.page || 1) * rowsPerPage
  )

  const visibleKeys = visibleTags.map(getGroupKey)

  const validKeys = useMemo(() => tagGroups.map(getGroupKey), [tagGroups])

  const selectedTagIds = useMemo(() => {
    return tagGroups
      .filter(tag => selectedTags.includes(getGroupKey(tag)))
      .reduce((acc, tag) => acc.concat(getAllTagIds(tag)), [])
  }, [tagGroups, selectedTags])

  const isAllSelected = visibleKeys.length > 0 && visibleKeys.every(key => selectedTags.includes(key))

  useEffect(() => {
    setSelectedTags(prev => {
      const next = prev.filter(key => validKeys.includes(key))

      return next.length === prev.length ? prev : next
    })
  }, [validKeys])

  useEffect(() => {
    const totalPages = rowsPerPage > 0
      ? Math.ceil(filteredTags.length / rowsPerPage)
      : 1

    setFilter(prev => {
      const safeTotalPages = totalPages || 1
      const currentPage = Number(prev.page || 1)

      if (currentPage > safeTotalPages) {
        return { ...prev, page: safeTotalPages }
      }

      if (currentPage < 1) {
        return { ...prev, page: 1 }
      }

      return prev
    })
  }, [filteredTags.length, rowsPerPage])

  const changeTypeFilter = nextType => () => {
    setTypeFilter(nextType)
    setSelectedTags([])
    setFilter(prev => ({ ...prev, page: 1 }))
  }

  const openNewTagGroup = () => {
    const nextSection = typeFilter === 'product' ? 'product' : 'machine'

    setFormSection(nextSection)
    openForm('new')()
  }

  const deleteLabel = label => () => {
    if (window.confirm(TRL_Pack.tags.deleteTag))
      fetchMssqlApi('tags', { method: 'DELETE', hideNotification: true, data: { Label: label } }, () => {
        NotificationManager.success('Usunięto grupę pomyślnie')
        setSelectedTags([])
        getTags()
      })
  }

  const toggleTag = tag => evt => {
    evt.stopPropagation()

    const key = getGroupKey(tag)

    setSelectedTags(prev => prev.includes(key)
      ? prev.filter(item => item !== key)
      : prev.concat(key)
    )
  }

  const toggleAll = () => {
    setSelectedTags(prev => {
      if (isAllSelected) {
        return prev.filter(key => !visibleKeys.includes(key))
      }

      return Array.from(new Set(prev.concat(visibleKeys)))
    })
  }

  const clearSelection = () => setSelectedTags([])

  const updateStatus = isActive => () => {
    if (!selectedTagIds.length) {
      NotificationManager.warning('Nie wybrano żadnych tagów')
      return
    }

    fetchMssqlApi(
      'tags/status',
      {
        method: 'PUT',
        hideNotification: true,
        data: {
          TagIds: selectedTagIds,
          IsActive: isActive ? 1 : 0
        }
      },
      () => {
        NotificationManager.success(
          isActive ? 'Aktywowano zaznaczone tagi' : 'Dezaktywowano zaznaczone tagi'
        )

        setSelectedTags([])
        getTags()
      }
    )
  }

  const handleModal = tag => evt => {
    evt.stopPropagation()
    setFormSection(tag.section)
    setTagLabel(tag.label)
    openForm('acceptModal')()
  }

  const handleEdit = tag => () => {
    setFormSection(tag.section)
    openForm(tag.others ? 'others' : tag.label)()
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)

      const screenHeight = window.innerHeight
      const reservedSpace = 350
      const visibleRows = Math.floor((screenHeight - reservedSpace) / 40)

      setFilter(prev => ({
        ...prev,
        rowsPerPage: visibleRows > 50 ? 50 : Math.max(10, visibleRows)
      }))
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    getTags()
    setHeaderData({ text: 'Grupy tagów' })
  }, [])

  return (
    <div>
      <SearchFilter
        filterName="tags"
        defaultFilter={defaultFilter}
        onFilterChange={f => setFilter(f)}
        onSettingsChange={s => setSettingsFilter(s)}
        search={getTags}
        totalCount={filteredTags.slice((Number(filter.page || 1) - 1) * rowsPerPage, Number(filter.page || 1) * rowsPerPage).length}
        dataList={filteredTags}
        forNow
      />

<div className="card">
  <div className="card-header">
    <div className="d-flex justify-content-between align-items-center flex-wrap">
      <div className="d-flex align-items-center flex-wrap">
        {selectedTags.length > 0 && (
          <div className="d-flex align-items-center flex-wrap">
            <button type="button" className="btn btn-outline-success mr-2 mt-1 mb-1" onClick={updateStatus(true)}>
              Aktywuj
            </button>

            <button type="button" className="btn btn-outline-danger mr-2 mt-1 mb-1" onClick={updateStatus(false)}>
              Dezaktywuj
            </button>

            <button type="button" className="btn btn-outline-secondary mr-2 mt-1 mb-1" onClick={clearSelection}>
              <i className="fas fa-times" />
            </button>
          </div>
        )}
      </div>

      <button type="button" className="btn text-nowrap btn-outline-secondary mt-1 mb-1" onClick={openNewTagGroup}>
        <i className="fas fa-plus" />
      </button>
    </div>
  </div>

  <div
    className="overflow-auto custom-scroll"
    style={{
      maxHeight: `${windowHeight - 216}px`,
      overflowY: 'auto'
    }}
  >
    <table className="table table-striped">
      <thead>
        <tr className="headers-color text-center">
          <th style={{ width: 45 }}>
            <input type="checkbox" checked={isAllSelected} onChange={toggleAll} />
          </th>

          <th className="pe-none" style={{ fontSize: '12px' }}>
            Nazwa grupy
          </th>

          <th style={{ fontSize: '12px' }}>
            <div className="d-flex flex-column align-items-center">
              {showColumnFilters && (
                <div className="d-flex justify-content-center mt-1">
                  <button
                    type="button"
                    className={`badge tags-font-size border-0 mr-1 ${typeFilter === 'all' ? 'badge-primary' : 'badge-light'}`}
                    onClick={changeTypeFilter('all')}
                    style={{ cursor: 'pointer' }}
                  >
                    {getTypeName('all')}
                  </button>

                  <button
                    type="button"
                    className={`badge tags-font-size border-0 mr-1 ${typeFilter === 'machine' ? 'badge-primary' : 'badge-light'}`}
                    onClick={changeTypeFilter('machine')}
                    style={{ cursor: 'pointer' }}
                  >
                    {getTypeName('machine')}
                  </button>

                  <button
                    type="button"
                    className={`badge tags-font-size border-0 ${typeFilter === 'product' ? 'badge-primary' : 'badge-light'}`}
                    onClick={changeTypeFilter('product')}
                    style={{ cursor: 'pointer' }}
                  >
                    {getTypeName('product')}
                  </button>
                </div>
              )}
            </div>
          </th>

          <th className="pe-none" style={{ fontSize: '12px' }}>
            Liczba tagów
          </th>

          <th className="pe-none" style={{ fontSize: '12px' }}>
            Aktywna
          </th>

          <th></th>
        </tr>
      </thead>

      <tbody>
        {visibleTags.length ? (
          visibleTags.map((tag, idx) => {
            const key = getGroupKey(tag)
            const allCount = (tag.options || []).length
            const activeCount = getActiveCount(tag)

            return (
              <tr key={key || idx}>
                <td className="text-center">
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(key)}
                    onChange={toggleTag(tag)}
                  />
                </td>

                <td className="text-center">{getTagName(tag)}</td>

                <td className="text-center">{getTypeName(tag.section)}</td>

                <td className="text-center">{allCount}</td>

                <td className="text-center">
                  <input
                    type="checkbox"
                    checked={allCount > 0 && activeCount === allCount}
                    readOnly
                    style={{ pointerEvents: 'none' }}
                  />
                </td>

                <td className="d-flex justify-content-end">
                  <button className="btn btn-outline-primary btn-sm" onClick={handleEdit(tag)}>
                    Edytuj
                  </button>
                </td>
              </tr>
            )
          })
        ) : (
<tr>
  <td></td>
  <td colSpan={4} className="text-center h3 p-4">
    Brak danych
  </td>
<td className="d-flex justify-content-end">
  <button
    className="btn btn-outline-primary btn-sm"
    style={{ visibility: 'hidden' }}
    disabled
  >
    Edytuj
  </button>
</td>
</tr>
        )}
      </tbody>
    </table>
  </div>
</div>

      {form === 'acceptModal' && form && (
        <AcceptForm handleClose={closeForm} deleteLabel={deleteLabel(tagLabel)} />
      )}

      {form !== 'acceptModal' && form && (
        <TagForm
          tagData={
            form === 'others'
              ? tags[formSection].find(label => label.others) || { section: formSection, others: true, options: [] }
              : form !== 'new'
                ? tags[formSection].find(tag => tag.label === form)
                : null
          }
          handleClose={closeForm}
          getTags={getTags}
          section={typeBySection[formSection]}
        />
      )}
    </div>
  )
}

export default Tags